import { useCallback } from 'react'
import { GridColDef } from '@mui/x-data-grid-pro'
import { useAppDispatch } from '@user/store/hooks'
import {
  EntityPreferenceType,
  getSaveListViewPreferenceFunc,
} from '@user/store/userPreferences'

export type FieldState = {
  field: string
  hide: boolean
  width?: string
}

interface UseAddMissingColumnsParams {
  columnOrder: FieldState[]
  columns: GridColDef[]
  newlyAddedColumns: string[]
  preference: EntityPreferenceType
}

/**
 * This hook is implemented to ensure that newly added columns are visible both in the export file
 * and in the list view. It does this by saving the newly added columns to the user preferences.
 */
export const useAddMissingColumns = ({
  columnOrder,
  columns,
  newlyAddedColumns,
  preference,
}: UseAddMissingColumnsParams) => {
  const dispatch = useAppDispatch()
  const saveListViewPreference = getSaveListViewPreferenceFunc(preference)

  const syncNewColumnsWithPreferences = useCallback(() => {
    if (!columnOrder || columnOrder.length === 0) return
    if (!columns || columns.length === 0) return

    // Find missing columns
    const missingColumns: FieldState[] = newlyAddedColumns
      .filter((key) => !columnOrder.some((col) => col.field === key))
      .map((field) => ({ field, hide: false }))

    if (missingColumns.length === 0) return

    // Merge existing + missing columns and map widths
    const mappedData = [...columnOrder, ...missingColumns].map((row) => {
      const found = columns.find((col) => col.field === row.field)
      return found?.width
        ? { ...row, width: found.width.toString() }
        : { ...row }
    })

    // Save updated preference
    saveListViewPreference(dispatch, mappedData)
  }, [
    columnOrder,
    columns,
    dispatch,
    newlyAddedColumns,
    saveListViewPreference,
  ])

  return {
    syncNewColumnsWithPreferences,
  }
}
